import React, { useState, useEffect } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Tab, TabPanel, Tabs } from '@sunwisesoftware/sunwise-ui';
import { useApiIsLoaded } from '@vis.gl/react-google-maps';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { POLYGON_SEGMENT } from 'common/components/segment/constants';
import {
    checkCallback,
    checkCallbackError,
} from 'common/components/segment/helpers';
import PanelsCard from 'common/components/segment/PanelsCard';
import { PROPOSAL_STATUS } from 'common/constants';

import * as actions from '../actions';
import * as selectors from '../selectors';

import ShaderSettingsForm from './forms/ShaderSettingsForm';
import SegmentsListCard from './SegmentsListCard';

const SegmentsBar = ({
    commercialOfferId,
    generateProposal,
    handleClickEdit,
    handleOnDuplicateSegment,
    isDisabledEdition,
    isDrawingMode,
    isEditionMode,
    isMobile,
    mapRef,
    offerInfoPaternData,
    offerPanels,
    offerPanelsCounters,
    onCloseDrawer,
    prepareDeleteSegment,
    prepareEditSegment,
    proposalId,
    redirect,
    renderStatus,
    resetForm,
    saveShaderSettings,
    segments,
    selectedSegmentId,
    selectedTab,
    selectSegment,
    setAddSegmentModalOpenStatus,
    setDrawingMode,
    setIsEnabledSimulation,
    setSelectedTab,
    toggleLockSegment,
    updateSegments,
}) => {
    const { t } = useTranslation();

    const [google, setGoogle] = useState(null);
    const apiIsLoaded = useApiIsLoaded();

    const handleOnChangeTab = (_, newValue) => {
        setSelectedTab(newValue);
        setIsEnabledSimulation(newValue !== 'design');
        resetForm();
    };
    const deleteCallback = (id) => {
        if (mapRef && mapRef.current) {
            mapRef.current.deleteShape(id);
        }
        onCloseDrawer();
    };

    useEffect(() => {
        if (!apiIsLoaded) return;
        setGoogle(window.google);
    }, [apiIsLoaded]);

    useEffect(() => {
        if (selectedTab === 'design') {
            saveShaderSettings({ isPlaying: false });
        }
    }, [selectedTab]);

    return (
        <Box px={1}>
            <Box py={1}>
                {renderStatus === PROPOSAL_STATUS.GENERATED ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            fullWidth
                            onClick={() =>
                                redirect(`/panel-layout/preview/${proposalId}`)
                            }
                        >
                            {t('Preview')}
                        </Button>

                        <Button
                            fullWidth
                            onClick={() =>
                                handleClickEdit(commercialOfferId, proposalId)
                            }
                            endIcon={<EditIcon />}
                            variant="outlined"
                        >
                            {t('Edit')}
                        </Button>
                    </Box>
                ) : (
                    <Button
                        fullWidth
                        onClick={() => generateProposal(proposalId)}
                    >
                        {t('Generate report')}
                    </Button>
                )}
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    centered
                    onChange={handleOnChangeTab}
                    scrollButtons="auto"
                    value={selectedTab}
                    variant="fullWidth"
                >
                    <Tab
                        id="tab-design"
                        key="tab-design"
                        label={t('Design')}
                        value="design"
                    />

                    <Tab
                        id="tab-simulation"
                        key="tab-simulation"
                        label={t('Simulation')}
                        value="simulation"
                    />
                </Tabs>
            </Box>

            <TabPanel key="tab-design" selectedTab={selectedTab} value="design">
                <>
                    <PanelsCard offerPanels={offerPanels} segments={segments} />

                    <SegmentsListCard
                        commercialOfferId={commercialOfferId}
                        handleClickAdd={({ type = null } = {}) => {
                            if (type === POLYGON_SEGMENT)
                                setAddSegmentModalOpenStatus(true);
                            else setDrawingMode(true, type);
                            onCloseDrawer();
                        }}
                        handleClickDelete={(id) =>
                            prepareDeleteSegment(
                                deleteCallback,
                                commercialOfferId,
                                id,
                                google,
                                mapRef.current.self(),
                                (id, modules) =>
                                    checkCallback(id, modules, mapRef),
                                (id, values) =>
                                    checkCallbackError(id, values, mapRef),
                            )
                        }
                        handleClickEditSegment={(value) =>
                            prepareEditSegment(value, google)
                        }
                        handleOnDuplicateSegment={handleOnDuplicateSegment}
                        handleSelectSegment={selectSegment}
                        isDisabledEdition={isDisabledEdition}
                        isDrawingMode={isDrawingMode}
                        isEditionMode={isEditionMode}
                        mapRef={mapRef}
                        offerInfoPaternData={offerInfoPaternData}
                        offerPanels={offerPanels}
                        offerPanelsCounters={offerPanelsCounters}
                        onCloseDrawer={onCloseDrawer}
                        segments={segments}
                        selectedSegmentId={selectedSegmentId}
                        toggleLockSegment={toggleLockSegment}
                        updateSegments={updateSegments}
                    />
                </>
            </TabPanel>

            <TabPanel
                key="tab-simulation"
                selectedTab={selectedTab}
                value="simulation"
            >
                <ShaderSettingsForm
                    isMobile={isMobile}
                    onCloseDrawer={onCloseDrawer}
                />
            </TabPanel>
        </Box>
    );
};

const mapStateToProps = createStructuredSelector({
    offerInfoPaternData: selectors.getOfferInfoPaternData,
    offerPanelsCounters: selectors.getOfferPanelsCounters,
    parentFieldSegmentCounters: selectors.getParentFieldSegmentCounters,
    selectedSegmentId: selectors.getSelectedSegmentId,
});

const mapDispatchToProps = (dispatch) => ({
    handleOnDuplicateSegment: (item, commercialOfferId) =>
        dispatch(actions.handleOnDuplicateSegment(item, commercialOfferId)),
    prepareDeleteSegment: (
        callback,
        commercialOfferId,
        id,
        google,
        mapValue,
        checkCallback,
        checkCallbackError,
    ) =>
        dispatch(
            actions.prepareDeleteSegment(
                callback,
                commercialOfferId,
                id,
                google,
                mapValue,
                checkCallback,
                checkCallbackError,
            ),
        ),
    prepareEditSegment: (values, google) =>
        dispatch(actions.setInitialValues(values, google)),
    redirect: (link) => dispatch(push(link)),
    resetForm: () => dispatch(actions.resetForm()),
    saveShaderSettings: (config) =>
        dispatch(actions.saveShaderSettings(config)),
    selectSegment: (segment) => dispatch(actions.selectSegment(segment)),
    setAddSegmentModalOpenStatus: (isOpen) =>
        dispatch(actions.setAddSegmentModalOpenStatus(isOpen)),
    toggleLockSegment: (id) => dispatch(actions.toggleLockSegment(id)),
    updateSegments: (values) => dispatch(actions.updateSegments(values)),
});

SegmentsBar.propTypes = {
    commercialOfferId: PropTypes.string,
    generateProposal: PropTypes.func,
    handleClickEdit: PropTypes.func,
    handleOnDuplicateSegment: PropTypes.func,
    isDisabledEdition: PropTypes.bool,
    isDrawingMode: PropTypes.bool,
    isEditionMode: PropTypes.bool,
    isMobile: PropTypes.bool,
    mapRef: PropTypes.object,
    offerInfoPaternData: PropTypes.object,
    offerPanels: PropTypes.array,
    offerPanelsCounters: PropTypes.object,
    onCloseDrawer: PropTypes.func,
    prepareDeleteSegment: PropTypes.func,
    prepareEditSegment: PropTypes.func,
    proposalId: PropTypes.string,
    redirect: PropTypes.func,
    renderStatus: PropTypes.string,
    resetForm: PropTypes.func,
    saveShaderSettings: PropTypes.func,
    segments: PropTypes.array,
    selectedSegmentId: PropTypes.string,
    selectedTab: PropTypes.string,
    selectSegment: PropTypes.func,
    setAddSegmentModalOpenStatus: PropTypes.func,
    setDrawingMode: PropTypes.func,
    setIsEnabledSimulation: PropTypes.func,
    setSelectedTab: PropTypes.func,
    toggleLockSegment: PropTypes.func,
    updateSegments: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(SegmentsBar);
